import { zodResolver } from "@hookform/resolvers/zod";
import {
  type AriaDateValue,
  Box,
  Flex,
  KdsCalendar,
  KdsClipboardButton,
  KdsHeader,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiCheckboxCircleFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";

import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FeatureFlagUpsertCommandFeatureEnum } from "@/config/apiClient";
import { RetroplanningStatusEnum } from "@/config/apiClient.types";
import { calendarDateToUTC, utcDateToLocalMidnight } from "@/helpers/dates";
import { getUserLocale } from "@/helpers/lang";
import {
  useGetIsFeatureActive,
  useOrders,
  useRetroplanning,
  useToasts,
} from "@/hooks";
import { useGetRetroplanningStatusAvailability } from "@/hooks/useGetRetroplanningStatusAvailability";
import { usePageTemplate } from "@/hooks/utils";
import { ModalCancelRetroplanning } from "@/modals/ModalCancelRetroplanning";
import { userAtom } from "@/store";
import { RetroplanningStep } from "./_partials/RetroplanningStep";
import {
  type InferredRetroplanningFormPageSchema,
  retroplanningFormPageSchema,
} from "./RetroplanningFormPage.schema";

export const RetroplanningFormPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { add } = useToasts();
  const [searchParams] = useSearchParams();
  const userAtomValue = useAtomValue(userAtom);

  const { PageTemplateV2, isScrolled } = usePageTemplate();
  const { orderId, orderItemId } = useParams() as {
    orderId: string;
    orderItemId: string;
  };
  const { isActive: isRetroplanningActive } = useGetIsFeatureActive(
    FeatureFlagUpsertCommandFeatureEnum.Retroplanning
  );

  const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
  const [currentStep, setCurrentStep] =
    useState<RetroplanningStatusEnum | null>(
      RetroplanningStatusEnum.QUOTE_TO_VALIDATE
    );

  const { useGetOrder } = useOrders();
  const { useAddRetroplanning, useUpdateRetroplanning } = useRetroplanning();
  const { data, isLoading, isError } = useGetOrder({ orderId });
  const {
    mutateAsync: addRetroplanning,
    isPending: isAddRetroplanningPending,
  } = useAddRetroplanning({
    orderId: orderId,
    orderItemId: orderItemId,
  });
  const {
    mutateAsync: updateRetroplanning,
    isPending: isUpdateRetroplanningPending,
  } = useUpdateRetroplanning({
    orderId: orderId,
    orderItemId: orderItemId,
  });

  const isEditionMode = !!searchParams.get("edit");
  const order = data?.data;
  const orderItem = order?.OrderItem.find((oi) => oi.id === orderItemId);

  const availableStatus = useGetRetroplanningStatusAvailability(
    order,
    orderItemId
  );

  const methods = useForm<InferredRetroplanningFormPageSchema>({
    resolver: zodResolver(retroplanningFormPageSchema),
    defaultValues: {
      QUOTE_TO_VALIDATE: null,
      HD_FILE_MISSING: null,
      PROOF_TO_VALIDATE: null,
      PURCHASE_ORDER_MISSING: null,
    },
  });

  // Set First focused Status
  useEffect(() => {
    if (!isEmpty(availableStatus)) {
      setCurrentStep(
        availableStatus.find((status) => !status.disabled && !status.hidden)
          ?.status ?? null
      );
    }
  }, [availableStatus]);

  // Set Form Default Values
  useEffect(() => {
    if (orderItem) {
      const quoteToValidateDueDate =
        orderItem.retroplanning?.retroplanningItems?.find(
          (rpi) => rpi.status === RetroplanningStatusEnum.QUOTE_TO_VALIDATE
        )?.dueDate ?? null;
      const hdFileMissingDueDate =
        orderItem.retroplanning?.retroplanningItems?.find(
          (rpi) => rpi.status === RetroplanningStatusEnum.HD_FILE_MISSING
        )?.dueDate ?? null;
      const proofToValidateDueDate =
        orderItem.retroplanning?.retroplanningItems?.find(
          (rpi) => rpi.status === RetroplanningStatusEnum.PROOF_TO_VALIDATE
        )?.dueDate ?? null;
      const purchaseOrderMissingDueDate =
        orderItem.retroplanning?.retroplanningItems?.find(
          (rpi) => rpi.status === RetroplanningStatusEnum.PURCHASE_ORDER_MISSING
        )?.dueDate ?? null;

      methods.reset({
        createdAt: dayjs(orderItem.createdAt).toDate(),
        deliveryDate: dayjs(orderItem.sandboxItem.deliveryDate).toDate(),
        QUOTE_TO_VALIDATE: quoteToValidateDueDate
          ? dayjs(quoteToValidateDueDate).toDate()
          : null,
        HD_FILE_MISSING: hdFileMissingDueDate
          ? dayjs(hdFileMissingDueDate).toDate()
          : null,
        PROOF_TO_VALIDATE: proofToValidateDueDate
          ? dayjs(proofToValidateDueDate).toDate()
          : null,
        PURCHASE_ORDER_MISSING: purchaseOrderMissingDueDate
          ? dayjs(purchaseOrderMissingDueDate).toDate()
          : null,
      });
    }
  }, [orderItem, methods.reset]);

  // Handle Date Change
  const handleDateChange = (date: Date | null) => {
    if (!currentStep) {
      return;
    }

    // Clear Error without Timeout if date is between createdAt and deliveryDate
    if (
      !isEmpty(methods.formState.errors) &&
      dayjs(date)?.isBetween(
        dayjs(methods.getValues("createdAt")),
        dayjs(methods.getValues("deliveryDate")),
        "day",
        "()"
      )
    ) {
      methods.clearErrors(currentStep);
    }

    // Set new date value
    if (currentStep) {
      methods.setValue(currentStep, date ?? null, { shouldDirty: true });
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    if (isEmpty(methods.formState.dirtyFields)) {
      void navigate(`/orders/${order?.id}?tab=retroplanning`);
    } else {
      setCancelModalIsOpen(true);
    }
  };

  // Handle Save Retroplanning (Update Or Create)
  const handleSaveRetroplanning = async () => {
    try {
      // Transform Form data to be send
      const retroplanningItemsToSend = Object.entries(
        methods.getValues()
      ).filter(
        ([key, value]) =>
          key !== "createdAt" && key !== "deliveryDate" && !!value
      );

      if (isEditionMode) {
        await updateRetroplanning(
          retroplanningItemsToSend.map(([key, value]) => ({
            status: key as RetroplanningStatusEnum,
            dueDate: value?.toUTCString() || "",
          }))
        );

        add({
          state: "success",
          title: t("oms.addRetroplanning.toasts.successUpdate"),
        });
      } else {
        await addRetroplanning(
          retroplanningItemsToSend.map(([key, value]) => ({
            status: key as RetroplanningStatusEnum,
            dueDate: value?.toUTCString() || "",
          }))
        );

        add({
          state: "success",
          title: t("oms.addRetroplanning.toasts.successCreate"),
        });
      }

      void navigate(`/orders/${order?.id}?tab=retroplanning`);
    } catch (error) {
      console.error(error);
      add({
        state: "error",
        title: isEditionMode
          ? t("oms.addRetroplanning.toasts.errorUpdate")
          : t("oms.addRetroplanning.toasts.errorCreate"),
      });
    }
  };

  if (!isRetroplanningActive) {
    return <Navigate to="/404" />;
  }

  if (isLoading || isError) {
    return null;
  }

  return (
    <PageTemplateV2>
      <FormProvider {...methods}>
        <form
          onSubmit={(e) =>
            void methods.handleSubmit(handleSaveRetroplanning)(e)
          }
          noValidate
        >
          <PageTemplateV2.Header>
            <KdsHeader
              backButtonProps={{ onPress: handleCancel }}
              buttons={[
                {
                  id: "cancel",
                  buttonType: "button",
                  onPress: handleCancel,
                  children: t("buttons.cancel"),
                  size: "small",
                  color: "neutral",
                  variant: "outlined",
                  disabled:
                    isUpdateRetroplanningPending || isAddRetroplanningPending,
                },
                {
                  id: "save",
                  buttonType: "button",
                  children: t("buttons.save"),
                  type: "submit",
                  size: "small",
                  isLoading:
                    isUpdateRetroplanningPending || isAddRetroplanningPending,
                  disabled:
                    isUpdateRetroplanningPending ||
                    isAddRetroplanningPending ||
                    (isEditionMode && isEmpty(methods.formState.dirtyFields)),
                },
              ]}
              isScrolled={isScrolled}
              metadatas={
                order?.referenceId
                  ? [
                      <KdsClipboardButton
                        key={order?.referenceId}
                        text={order?.referenceId}
                        alt={t("clipboard.orderId")}
                        displayToast={() =>
                          add({
                            key: order?.referenceId,
                            state: "reverse",
                            title: t("toast.clipboard.orderId"),
                          })
                        }
                      />,
                    ]
                  : []
              }
              title={order?.name}
            />
          </PageTemplateV2.Header>

          <PageTemplateV2.Content px="sp-xl3" py="sp-xl">
            <Flex gap="sp-sm" align="flex-start" wrap="nowrap" w="100%">
              <Box
                w="25%"
                minW="min-content"
                h="100%"
                rounded="br-xl"
                bgColor="elevation.first"
                px="sp-xs"
                py="sp-sm"
              >
                <Flex direction="column" gap="sp-sm">
                  <Box w="100%" rounded="br-sm" py="sp-sm" px="sp-md">
                    <Flex
                      direction="row"
                      gap="sp-sm"
                      justify="space-between"
                      align="center"
                    >
                      <Flex direction="column" gap="sp-xs2">
                        <KdsText size="subtitle-sm">
                          {t("cardRetroplanning.status.PLACED")}
                        </KdsText>

                        <KdsText size="body-sm" color="text-bodyvariant">
                          {dayjs(orderItem?.sandboxItem.createdAt).format("L")}
                        </KdsText>
                      </Flex>

                      <RiCheckboxCircleFill className="size-si-sm text-success-main" />
                    </Flex>
                  </Box>

                  {availableStatus.map((item) => {
                    return (
                      <RetroplanningStep
                        key={item.status}
                        item={item}
                        isCurrentStep={currentStep === item.status}
                        onClick={setCurrentStep}
                      />
                    );
                  })}

                  <Box w="100%" rounded="br-sm" py="sp-sm" px="sp-md">
                    <Flex
                      direction="row"
                      gap="sp-sm"
                      justify="space-between"
                      align="center"
                    >
                      <Flex direction="column" gap="sp-xs2">
                        <KdsText size="subtitle-sm">
                          {t("cardRetroplanning.status.DELIVERY_DATE")}
                        </KdsText>

                        <KdsText size="body-sm" color="text-bodyvariant">
                          {dayjs(orderItem?.sandboxItem.deliveryDate).format(
                            "L"
                          )}
                        </KdsText>
                      </Flex>

                      <RiCheckboxCircleFill className="size-si-sm text-success-main" />
                    </Flex>
                  </Box>

                  {!!methods.formState.errors && (
                    <Box w="100%" rounded="br-sm" py="sp-sm" px="sp-md">
                      <KdsText size="caption-sm" color="error-text">
                        {Object.values(methods.formState.errors)[0]?.message}
                      </KdsText>
                    </Box>
                  )}
                </Flex>
              </Box>

              <Box
                w="75%"
                minW="470px"
                mb="auto"
                rounded="br-xl"
                bgColor="elevation.first"
                p="sp-md"
              >
                {Object.values(RetroplanningStatusEnum).map((status) => {
                  if (status !== currentStep) {
                    return null;
                  }

                  return (
                    <Controller
                      key={status}
                      control={methods.control}
                      name={status}
                      render={({ field, fieldState }) => (
                        <KdsCalendar
                          key={field.value?.getTime()}
                          selectedDate={
                            field.value
                              ? utcDateToLocalMidnight(field.value)
                              : undefined
                          }
                          locale={getUserLocale({
                            locale: userAtomValue?.preferredLanguage,
                          })}
                          isSelectedDateError={
                            currentStep ? !!fieldState.error : false
                          }
                          firstAvailableDate={dayjs(
                            orderItem?.sandboxItem.createdAt
                          )
                            .add(1, "day")
                            .toDate()}
                          lastAvailableDate={dayjs(
                            orderItem?.sandboxItem.deliveryDate
                          )
                            .subtract(1, "day")
                            .toDate()}
                          onChange={(v: AriaDateValue | null) => {
                            handleDateChange(v ? calendarDateToUTC(v) : null);
                          }}
                        />
                      )}
                    />
                  );
                })}
              </Box>
            </Flex>
          </PageTemplateV2.Content>

          <ModalCancelRetroplanning
            isOpen={cancelModalIsOpen}
            onOpenChange={() => setCancelModalIsOpen(false)}
            onQuit={() =>
              void navigate(`/orders/${order?.id}?tab=retroplanning`)
            }
          />
        </form>
      </FormProvider>
    </PageTemplateV2>
  );
};
